/**
 * Local backend adapter (uses localStorage for dev/testing)
 */

import type { Backend, Document, NoCodeComponentEntry } from "@easyblocks/core";

export const DEMO_DOCUMENT_ID = "demo-page";

const STORAGE_PREFIX = "easyblocks-demo";
const docKey = (id: string) => `${STORAGE_PREFIX}:document:${id}`;

const createDefaultEntry = (): NoCodeComponentEntry => ({
  _component: "RootLayoutSections",
  _id: "easyblocks-root-stack",
  Sections: [],
});

const templatesDisabled: Backend["templates"] = {
  get: async (payload) => {
    throw new Error(`Templates are not supported (requested: ${payload.id})`);
  },
  getAll: async () => [],
  create: async () => {
    throw new Error("Templates are not supported");
  },
  update: async () => {
    throw new Error("Templates are not supported");
  },
  delete: async () => {
    throw new Error("Templates are not supported");
  },
};

function safeParse<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function readDocument(id: string): Document | null {
  if (typeof window === "undefined") return null;
  return safeParse<Document>(window.localStorage.getItem(docKey(id)));
}

function writeDocument(doc: Document) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(docKey(doc.id), JSON.stringify(doc));
}

function ensureDocument(id: string): Document {
  const existing = readDocument(id);
  if (existing) return existing;
  const created: Document = { id, version: 1, entry: createDefaultEntry() };
  writeDocument(created);
  return created;
}

export const localBackend: Backend = {
  documents: {
    get: async (payload: { id: string; locale?: string }): Promise<Document> => {
      return ensureDocument(payload.id);
    },
    create: async (payload: Omit<Document, "id" | "version">): Promise<Document> => {
      const id = `${DEMO_DOCUMENT_ID}-${Date.now()}`;
      const created: Document = { id, version: 1, entry: payload.entry as NoCodeComponentEntry };
      writeDocument(created);
      return created;
    },
    update: async (payload: Omit<Document, "type">): Promise<Document> => {
      const current = ensureDocument(payload.id);
      const nextVersion = (current.version ?? 0) + 1;
      const updated: Document = {
        id: payload.id,
        version: nextVersion,
        entry: payload.entry as NoCodeComponentEntry,
      };
      writeDocument(updated);
      return updated;
    },
  },
  templates: templatesDisabled,
};
